import type { LogoScene } from "../three/logoScene";
import type { HeroContent } from "../i18n/types";

export interface HeroHandles {
  root: HTMLElement;
  canvasMount: HTMLElement;
}

/** Newsbar item hrefs — structural, zipped with the news items in the
 * content dictionary. Order matches the dictionary: works-for, security,
 * models, features, consortium, resources. */
const NEWS_HREFS = ["#works-for", "#security", "#models", "#features", "#consortium", "#resources"] as const;

export function renderHero(t: HeroContent): HeroHandles {
  const root = document.createElement("section");
  root.className = "hero";
  root.id = "hero";
  root.innerHTML = `
    <div class="page">
      <div class="hero__newsbar" aria-label="${t.newsbarAriaLabel}">
        <span class="hero__newsLabel">${t.newsLabel}</span>
        <div class="hero__newsItems">
          ${t.newsItems.map((item, i) => `<a class="hero__newsItem" href="${NEWS_HREFS[i]}">${item}</a>`).join("")}
        </div>
        <span class="hero__newsArrow" aria-hidden="true">↗</span>
      </div>
      <div class="hero__inner">
        <div class="hero__copy">
          <p class="hero__eyebrow" data-reveal>
            ${t.eyebrowLead} <strong>${t.eyebrowName}</strong>
          </p>
          <h1 class="hero__title" data-reveal>
            ${t.titleLead}
            <span class="grad">${t.titleAccent}</span>
          </h1>
          <p class="hero__titleSub" data-reveal style="--reveal-delay:40ms">
            ${t.titleSub}
          </p>
          <p class="hero__subtitle" data-reveal style="--reveal-delay:80ms">
            ${t.subtitle}
          </p>
          <div class="hero__ctas" data-reveal style="--reveal-delay:160ms">
            <a class="btn btn--primary btn--primary--lg" href="https://cloud.sdu.dk/app">${t.ctaPrimary}</a>
            <a class="btn btn--ghost btn--primary--lg" href="mailto:escience@sdu.dk">${t.ctaSecondary}</a>
          </div>
          <p class="hero__fine" data-reveal style="--reveal-delay:220ms">
            ${t.finePrint}
            <br />
            ${t.finePrintSecondary}
          </p>
        </div>
        <div class="hero__stage" data-reveal style="--reveal-delay:280ms">
          <div class="hero__stageGlow"></div>
          <div id="logo-canvas" class="hero__canvas" aria-hidden="true"></div>
        </div>
      </div>
    </div>
  `;

  const canvasMount = root.querySelector("#logo-canvas") as HTMLElement;
  return { root, canvasMount };
}

/* Attach the Three.js scene after mount. Kept separate so the main entry owns
 * the scene lifecycle (pause on scroll, dispose on unload). */
export function attachLogoScene(
  canvasMount: HTMLElement,
  factory: (mount: HTMLElement) => LogoScene,
): LogoScene {
  return factory(canvasMount);
}
